import { inject, Injectable } from "@angular/core";
import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { _HttpClient } from "@delon/theme";

@Injectable({ providedIn: 'root' })
export class EinvLockFieldsService {
  private static readonly EXEMPTED = 3;
  private http = inject(_HttpClient);

  /**
   * Exempted → always editable.
   * Other controls → editable only when UIN is already assigned and status is beyond Pending.
   */
  static isUinEditable(eInvoiceControl: number, currentUin: string, currentStatus: string): boolean {
    if (eInvoiceControl === EinvLockFieldsService.EXEMPTED) return true;
    return !!currentUin && !!currentStatus && currentStatus !== 'Pending';
  }

  /**
   * Prevents clearing an assigned UIN on non-Exempted documents.
   * Returns null (valid) when Exempted or when UIN has a value.
   */
  static uinClearValidator(originalUin: string, eInvoiceControl: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (eInvoiceControl === EinvLockFieldsService.EXEMPTED) return null;
      if (originalUin && !control.value) {
        return { uinRequired: true };
      }
      return null;
    };
  }

  public toggleUinField(form: FormGroup | any, eInvoiceControl: number, originalUin: string, currentStatus: string): void {
    const uinCtrl = form?.controls?.['uin'];
    if (!uinCtrl) return;
    const editable = EinvLockFieldsService.isUinEditable(eInvoiceControl, originalUin, currentStatus);
    if (editable) {
      uinCtrl.enable();
      uinCtrl.setValidators(EinvLockFieldsService.uinClearValidator(originalUin, eInvoiceControl));
    } else {
      uinCtrl.disable();
      uinCtrl.clearValidators();
    }
    uinCtrl.updateValueAndValidity();
  }

  public toggleLockField( form: FormGroup | any, einvSubmitted: boolean ) {

    if (!form) return;    
    
    const commonFields = [
      'eInvoiceControl',
      'docDate',
      'docCode',
      'customer',
      'customerName',
      'billingAddress',
      'customerPhone',
      'billingPeriodTo',
      'billingPeriodFrom',
      'frequencyOfBilling',
      'customsRefNo',
      'incoterms',
      'isTaxInclusive',
      'stockLocation',
      'currency',
      'currencyRate',
      // einv info
      'detailsTaxExemption',
      'tinNumber',
      // shipping info
      'ftaInfo',
      'originCountry',
      'certifiedExporterNo',
      'otherCharges',
      'customsFormNo2',
      // customer info pop-up
      'customerName',
      'customerTinType',
      'registrationNo',
      'tinNumber',
      'salesTaxRegNo',
      'serviceTaxRegNo',
      'country',
      'state',
      'zipCode',
      'city',
      'address1',
      'address2',
      'address3',
      'address4',
      'customerPhone',
      'email',
      'sinvRef',
      'pinvRef'
    ];

    const lineItemFields = [
      'stock',
      'description',
      'classification',
      'qty',
      'unitPrice',
      'uom',
      'amount',
      'netAmount',
      'taxCode',
      'taxAmount',
      'discount',
      'discountAmount',
      'stockLocation',
      'taxExemptionDetails',
      'tariffCode',
    ];

    if (einvSubmitted) {
      // Skip fields that are not found in the form
      commonFields.forEach(fieldName => {
        if (form.controls[fieldName]) {
          // Disable if the control exists
          form.controls[fieldName].disable();
        }
      });

      const itemDetails = form.get('itemDetails') as FormArray;
      if (itemDetails) {
        itemDetails.controls.forEach((lineControl: AbstractControl) => {
          const lineGroup = lineControl as FormGroup; // Cast AbstractControl to FormGroup

          lineItemFields.forEach(fieldName => {
            if (lineGroup.controls[fieldName]) {
              lineGroup.controls[fieldName].disable();
            }
          });
        });
      }
    }
    else {
      commonFields.forEach(fieldName => {
        if (form.controls[fieldName]) {
          // Disable if the control exists
          form.controls[fieldName].enable();
        }
      });

      const itemDetails = form.get('itemDetails') as FormArray;
      if (itemDetails) {
        itemDetails.controls.forEach((lineControl: AbstractControl) => {
          const lineGroup = lineControl as FormGroup; // Cast AbstractControl to FormGroup

          lineItemFields.forEach(fieldName => {
            if (lineGroup.controls[fieldName]) {
              lineGroup.controls[fieldName].enable();
            }
          });
        });
      }
    }   
  }
}